import {
  ANTIGUEDAD_SCALE,
  EMPLEADOS_SCALE,
  FACTURACION_SCALE,
  FREE_LISTING_TYPE,
  MANDATO_LISTING_TYPE,
  MODALIDAD_OPTIONS,
  PROVINCIA_OPTIONS,
  PROVINCIA_TODA_ESPANA,
  TIPO_OPERACION_OPTIONS,
  listingFields,
} from '../config/configListing';
import { saleOperationValues } from './saleOperations';

const validOption = (value, options) => options.some(option => option.option === value);
const ordinal = (data, key, scale, field, property) => {
  const entry = scale.find(item => item[property] === data[field]);
  return entry && (data[key] == null || data[key] === entry.idx) ? entry.idx : null;
};

/** Build equivalent URL/API filters for an owner's complete sale listing.
 * EBITDA is intentionally not compared: sale listings do not declare it.
 */
export const matchingBuyerSearch = (listing, currentUser) => {
  const authorId = listing?.relationships?.author?.data?.id?.uuid || listing?.author?.id?.uuid;
  const userId = currentUser?.id?.uuid;
  const data = listing?.attributes?.publicData || {};
  if (
    !userId ||
    authorId !== userId ||
    data.listingType !== FREE_LISTING_TYPE ||
    !['published', 'closed'].includes(listing?.attributes?.state)
  )
    return null;

  const sectors = listingFields.find(field => field.key === 'sector').enumOptions;
  const parent = sectors.find(
    option =>
      option.option === data.sector ||
      option.suboptions?.some(child => child.option === data.sector)
  );
  // A parent with children does not identify the business's actual subsector.
  if (!parent || (parent.option === data.sector && parent.suboptions?.length)) return null;
  const operations = saleOperationValues(data.tipo_operacion);
  if (
    !validOption(data.provincia, PROVINCIA_OPTIONS) ||
    !validOption(data.modalidad, MODALIDAD_OPTIONS) ||
    !operations.length ||
    !operations.every(operation => validOption(operation, TIPO_OPERACION_OPTIONS))
  )
    return null;

  const ranges = [
    [
      'facturacionIdx',
      FACTURACION_SCALE,
      'facturacion',
      'option',
      'facturacionIdxMin',
      'facturacionIdxMax',
    ],
    [
      'antiguedadIdx',
      ANTIGUEDAD_SCALE,
      'antiguedad',
      'option',
      'antiguedadIdxMin',
      'antiguedadIdxMax',
    ],
    ['empleadosIdx', EMPLEADOS_SCALE, 'empleados', 'lowerBound', 'empleadosMin', 'empleadosMax'],
  ];
  const searchParams = {
    pub_sector: [...new Set([data.sector, parent.option])].join(','),
    pub_provincias: `has_any:${data.provincia},${PROVINCIA_TODA_ESPANA}`,
    pub_modalidades: `has_any:${data.modalidad}`,
    pub_tiposOperacion: `has_any:${[...new Set(operations)].join(',')}`,
  };
  const apiParams = {
    ...searchParams,
    pub_listingType: MANDATO_LISTING_TYPE,
    // Same stock policy used by SearchPage for searches without dates.
    minStock: 1,
    stockMode: 'match-undefined',
  };
  for (const [key, scale, field, property, minKey, maxKey] of ranges) {
    const point = ordinal(data, key, scale, field, property);
    if (point == null) return null;
    const first = scale[0].idx;
    const last = scale[scale.length - 1].idx;
    searchParams[`pub_${minKey}`] = `${first},${point}`;
    searchParams[`pub_${maxKey}`] = `${point},${last}`;
    // Sharetribe API long upper bounds are exclusive; URL filters are inclusive.
    apiParams[`pub_${minKey}`] = `${first},${point + 1}`;
    apiParams[`pub_${maxKey}`] = `${point},${last + 1}`;
  }
  return { searchParams, apiParams };
};
